// useDaily, React hook around fetchDaily()
//
// Estado:
//   { daily, loading, error, refresh }
//
// Comportamentos:
//   - SWR-style: cache em localStorage é devolvido imediatamente se for de hoje
//   - Revalida em background ao montar
//   - Re-busca quando a aba volta a foco (visibilitychange) E o dia mudou
//   - Reage à mudança de online/offline para tentar refresh ao voltar
//
// Falha silenciosamente: se a API estiver indisponível, `daily` permanece
// null (ou cache stale) e o componente decide o fallback. Zero exceptions
// vazadas para o tree.
import { useCallback, useEffect, useRef, useState } from "react";
import { fetchDaily } from "./dailyApi";

function _todayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function useDaily() {
  const [daily, setDaily] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const lastKeyRef = useRef(_todayKey());
  const aliveRef = useRef(true);
  const dailyRef = useRef(null);

  useEffect(() => {
    dailyRef.current = daily;
  }, [daily]);

  const load = useCallback(async (forceFresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const d = await fetchDaily({
        forceFresh,
        // Callback disparado APENAS quando a revalidação em background detecta
        // novo cache_key/updated_at (nova versão publicada pela landing).
        // Atualiza a UI sem esperar o próximo mount.
        onFresh: (fresh) => {
          if (!aliveRef.current) return;
          setDaily(fresh);
        },
      });
      if (!aliveRef.current) return;
      setDaily(d);
      if (!d) setError("unavailable");
    } catch (e) {
      if (!aliveRef.current) return;
      setError(e?.message || "unavailable");
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    aliveRef.current = true;
    load(false);

    const onVisibility = () => {
      if (document.visibilityState !== "visible") return;
      const today = _todayKey();
      if (today !== lastKeyRef.current) {
        // Dia mudou enquanto a aba estava em background → recarregar.
        lastKeyRef.current = today;
        load(true);
      }
    };
    const onOnline = () => load(false);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("online", onOnline);

    // Auto-refresh à meia-noite local. Agenda um timeout até o próximo
    // 00:00:30 e dispara `load(true)` para pegar o novo day-key.
    const now = new Date();
    const next = new Date(now);
    next.setHours(24, 0, 30, 0); // 00:00:30 do dia seguinte
    const ms = next.getTime() - now.getTime();
    const midnightTimer = window.setTimeout(() => {
      lastKeyRef.current = _todayKey();
      load(true);
    }, ms);

    // Rede de segurança: timers podem ser congelados pelo SO (PWA em
    // background, aba suspensa). Checagem leve a cada 60s: se o dia virou
    // ou os dados exibidos ficaram de um dia anterior, recarrega.
    const staleWatch = window.setInterval(() => {
      const today = _todayKey();
      const dataDate = dailyRef.current?.date;
      if (today !== lastKeyRef.current || (dataDate && dataDate !== today)) {
        lastKeyRef.current = today;
        load(true);
      }
    }, 60000);

    return () => {
      aliveRef.current = false;
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("online", onOnline);
      window.clearTimeout(midnightTimer);
      window.clearInterval(staleWatch);
    };
  }, [load]);

  return {
    daily,
    loading,
    error,
    refresh: () => load(true),
  };
}
